import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  fetchRoles,
  fetchGroups,
  fetchLocations,
} from "../../actions/users/managerAsyncActionsV2/managerAsyncActionsV2";
import { createManagerUsers } from "../../actions/users/managerAsyncActionsV2/managerCreationAsyncActionsV2";
import { logOut } from "../layout";

export type RowErrorType =
  | "invalid_format"
  | "duplicate"
  | "already_exists"
  | "missing_role";

export interface NewUserRow {
  id: string;
  email: string;
  siteId: string | null;
  siteName: string | null;
  role: string | null;
  roleName: string | null;
  assignedGroups: number[];
  groupsSelected: GroupOption[];
  groupsLocationsSelectedIds: number[];
  status: "vide" | "valid" | "existing" | "error";
  errorType?: RowErrorType;
}

interface LocaleInfo {
  id: number;
  value: string;
  originTableName: string;
  createdBy: string;
}

interface LocalizedText {
  id: string;
  textId: number;
  localeConstantId: number;
  translation: string;
  locale: LocaleInfo;
}

interface LocalizedTextContainer {
  id: number;
  slug: string;
  originTableName: string;
  localizedTexts: LocalizedText[];
}

interface LocalizedTextRef {
  id: number;
  textSlug: string;
  text: string;
  locale: Pick<LocaleInfo, "id" | "value">;
}

export interface RoleOptionRaw {
  createdAt: string;
  updatedAt: string;
  id: number;
  slug: string;
  nameTextId: LocalizedTextRef;
  descTextId: LocalizedTextRef;
  groupTemplateIds: number[];
  name: LocalizedTextContainer;
  desc: LocalizedTextContainer;
  nameTextIdLocale: string;
  descTextIdLocale: string;
}

export interface RoleOption {
  id: string;
  slug: string;
  name: string;
  desc: string;
  groupTemplateIds: number[];
}

export interface GroupOption {
  id: number;
  name: string;
  typeId: number;
  orgId: number;
  groupTemplateId: number;
  isAutoGenerated: boolean;
  locations: number[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LocationOption {
  id: number;
  name: string;
}

export interface SubmitResult {
  rowId: string;
  email: string;
  success: boolean;
  errorMessage?: string;
}

export interface ManagerCreationState {
  rows: NewUserRow[];
  reviewOpen: boolean;
  existingEmails: string[];
  roles: RoleOption[];
  rolesLoading: boolean;
  rolesError: string;
  groups: GroupOption[];
  groupsLoading: boolean;
  groupsError: string;
  locations: LocationOption[];
  locationsLoading: boolean;
  locationsError: string;
  submitLoading: boolean;
  submitSuccess: boolean;
  submitError: string;
  submitResults: SubmitResult[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const recomputeStatuses = (
  rows: NewUserRow[],
  existingEmails: string[] = [],
): NewUserRow[] => {
  const emailCounts = rows.reduce<Record<string, number>>((acc, r) => {
    const e = r.email.trim().toLowerCase();
    if (e) acc[e] = (acc[e] || 0) + 1;
    return acc;
  }, {});

  return rows.map((r) => {
    const email = r.email.trim();
    if (!email) return { ...r, status: "vide", errorType: undefined };
    if (!EMAIL_RE.test(email))
      return { ...r, status: "error", errorType: "invalid_format" };
    if (emailCounts[email.toLowerCase()] > 1)
      return { ...r, status: "error", errorType: "duplicate" };
    if (existingEmails.some((e) => e.toLowerCase() === email.toLowerCase()))
      return { ...r, status: "existing", errorType: "already_exists" };
    if (!r.role) return { ...r, status: "error", errorType: "missing_role" };
    return { ...r, status: "valid", errorType: undefined };
  });
};

export const makeEmptyRow = (): NewUserRow => ({
  id: uuidv4(),
  email: "",
  siteId: null,
  siteName: null,
  role: null,
  roleName: null,
  assignedGroups: [],
  groupsSelected: [],
  groupsLocationsSelectedIds: [],
  status: "vide",
  errorType: undefined,
});

export const initialState: ManagerCreationState = {
  rows: [makeEmptyRow()],
  reviewOpen: false,
  existingEmails: [],
  roles: [],
  rolesLoading: false,
  rolesError: "",
  groups: [],
  groupsLoading: false,
  groupsError: "",
  locations: [],
  locationsLoading: false,
  locationsError: "",
  submitLoading: false,
  submitSuccess: false,
  submitError: "",
  submitResults: [],
};

export const managerCreationSlice = createSlice({
  name: "managerCreation",
  initialState,
  reducers: {
    setExistingEmails(state, action: PayloadAction<string[]>) {
      state.existingEmails = action.payload;
      state.rows = recomputeStatuses([...state.rows], state.existingEmails);
    },
    addRow(state) {
      state.rows = recomputeStatuses(
        [...state.rows, makeEmptyRow()],
        state.existingEmails,
      );
    },
    deleteRow(state, action: PayloadAction<string>) {
      state.rows = recomputeStatuses(
        state.rows.filter((r) => r.id !== action.payload),
        state.existingEmails,
      );
    },
    updateEmail(state, action: PayloadAction<{ id: string; value: string }>) {
      state.rows = recomputeStatuses(
        state.rows.map((r) =>
          r.id === action.payload.id
            ? { ...r, email: action.payload.value }
            : r,
        ),
        state.existingEmails,
      );
    },
    updateSite(
      state,
      action: PayloadAction<{
        id: string;
        siteId: string | null;
        siteName: string | null;
      }>,
    ) {
      state.rows = recomputeStatuses(
        state.rows.map((r) =>
          r.id === action.payload.id
            ? {
                ...r,
                siteId: action.payload.siteId,
                siteName: action.payload.siteName,
              }
            : r,
        ),
        state.existingEmails,
      );
    },
    updateRole(
      state,
      action: PayloadAction<{
        id: string;
        role: string | null;
        roleName: string | null;
      }>,
    ) {
      state.rows = recomputeStatuses(
        state.rows.map((r) =>
          r.id === action.payload.id
            ? {
                ...r,
                role: action.payload.role,
                roleName: action.payload.roleName,
                assignedGroups: [],
                groupsSelected: [],
                groupsLocationsSelectedIds: [],
              }
            : r,
        ),
        state.existingEmails,
      );
    },
    updateGroups(
      state,
      action: PayloadAction<{ id: string; groups: GroupOption[] }>,
    ) {
      const selected = action.payload.groups;
      const allLocations = selected.flatMap((g) => g.locations);
      const uniqueLocations = [...new Set(allLocations)];

      state.rows = recomputeStatuses(
        state.rows.map((r) =>
          r.id === action.payload.id
            ? {
                ...r,
                assignedGroups: selected.map((g) => g.id),
                groupsSelected: selected,
                groupsLocationsSelectedIds: uniqueLocations,
              }
            : r,
        ),
        state.existingEmails,
      );
    },
    setReviewOpen(state, action: PayloadAction<boolean>) {
      state.reviewOpen = action.payload;
    },
    resetSubmitState(state) {
      state.submitLoading = false;
      state.submitSuccess = false;
      state.submitError = "";
      state.submitResults = [];
    },
    removeSuccessfulRows(state) {
      const successIds = new Set(
        state.submitResults.filter((r) => r.success).map((r) => r.rowId),
      );
      state.rows = recomputeStatuses(
        state.rows.filter((r) => !successIds.has(r.id)),
        state.existingEmails,
      );
      state.submitResults = [];
      state.submitSuccess = false;
    },
    resetManagerCreation() {
      return { ...initialState, rows: [makeEmptyRow()] };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = "";
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action: any) => {
        state.rolesLoading = false;
        state.rolesError =
          action.payload?.response?.data?.message || action?.payload || "";
      });

    builder
      .addCase(fetchGroups.pending, (state) => {
        state.groupsLoading = true;
        state.groupsError = "";
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.groupsLoading = false;
        state.groups = action.payload;
      })
      .addCase(fetchGroups.rejected, (state, action: any) => {
        state.groupsLoading = false;
        state.groupsError =
          action.payload?.response?.data?.message || action?.payload || "";
      });

    builder
      .addCase(fetchLocations.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = "";
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.locations = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action: any) => {
        state.locationsLoading = false;
        state.locationsError =
          action.payload?.response?.data?.message || action?.payload || "";
      });

    builder
      .addCase(createManagerUsers.pending, (state) => {
        state.submitLoading = true;
        state.submitError = "";
        state.submitSuccess = false;
      })
      .addCase(createManagerUsers.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.submitSuccess = true;
        state.submitResults = action.payload as SubmitResult[];
      })
      .addCase(createManagerUsers.rejected, (state, action: any) => {
        state.submitLoading = false;
        state.submitError =
          action.payload ?? action.error?.message ?? "Unknown error";
      });

    builder.addCase(logOut, () => initialState);
  },
});

export const {
  setExistingEmails,
  addRow,
  deleteRow,
  updateEmail,
  updateSite,
  updateRole,
  updateGroups,
  setReviewOpen,
  resetSubmitState,
  removeSuccessfulRows,
  resetManagerCreation,
} = managerCreationSlice.actions;

export const ManagerCreation = managerCreationSlice.reducer;
